/**
 * LabDocumentAttachments — file uploader + list for a single lab_result row.
 *
 * Renders inside the Labs table as an expandable per-row panel. Handles:
 *   - File picker (PDF, JPG, PNG, TIFF, plain text, CSV) + drag-and-drop
 *   - Client-side validation (type, size 25 MiB cap matching the server)
 *   - Base64-encode + POST to db:lab-documents:upload IPC channel
 *   - List existing attachments with download (presigned URL) + delete
 *   - PHI-safe: shows filename/size/sha; the file body never sits in
 *     React state longer than the upload roundtrip
 *
 * Server side: routes/clinical.js — db:lab-documents:upload/list/
 * download-url/delete (migration 092).
 */
import React, { useState, useEffect, useCallback, useRef } from "react";
import { Upload, Download, Trash2, FileText, AlertCircle, CheckCircle2, Loader2 } from "lucide-react";

const ALLOWED_TYPES = new Set([
  "application/pdf",
  "image/jpeg", "image/jpg", "image/png",
  "image/tiff", "image/tif",
  "text/plain", "text/csv",
]);
const ALLOWED_EXT_LABEL = "PDF, JPG, PNG, TIFF, TXT, CSV";
const MAX_SIZE = 25 * 1024 * 1024;  // 25 MiB

function humanSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || "");
      const comma = result.indexOf(",");
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(new Error("file read failed"));
    reader.readAsDataURL(file);
  });
}

export default function LabDocumentAttachments({ labResultId, canUpload, canDelete }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(null);  // 'reading', 'uploading'
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const rows = await window.zignagenixAPI.invoke("db:lab-documents:list", labResultId);
      setDocs(Array.isArray(rows) ? rows : []);
    } catch (e) {
      setError((e && e.message) || "Failed to load documents");
    }
    setLoading(false);
  }, [labResultId]);

  useEffect(() => { load(); }, [load]);

  async function uploadFile(file) {
    setError(null);
    setSuccess(null);
    if (!ALLOWED_TYPES.has(file.type)) {
      setError(`File type "${file.type || "unknown"}" not allowed. Permitted: ${ALLOWED_EXT_LABEL}.`);
      return;
    }
    if (file.size > MAX_SIZE) {
      setError(`File ${humanSize(file.size)} exceeds 25 MB limit.`);
      return;
    }
    if (file.size === 0) {
      setError("File is empty.");
      return;
    }
    setUploading(true);
    setProgress("reading");
    try {
      const fileBase64 = await fileToBase64(file);
      setProgress("uploading");
      const resp = await window.zignagenixAPI.invoke("db:lab-documents:upload", {
        labResultId,
        filename: file.name,
        mimeType: file.type,
        fileBase64,
      });
      if (resp && resp.error) {
        setError(resp.message || resp.code || "Upload failed");
      } else {
        setSuccess(`Uploaded ${file.name}`);
        await load();
      }
    } catch (e) {
      setError((e && e.message) || "Upload failed");
    }
    setProgress(null);
    setUploading(false);
  }

  async function handleFileInput(e) {
    const f = e.target.files && e.target.files[0];
    if (f) await uploadFile(f);
    e.target.value = "";  // reset so the same file can be re-selected
  }

  async function handleDrop(e) {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0];
    if (f) await uploadFile(f);
  }

  async function handleDownload(docId, filename) {
    setError(null);
    try {
      const resp = await window.zignagenixAPI.invoke("db:lab-documents:download-url", docId);
      if (resp && resp.error) {
        setError(resp.message || "Download failed");
        return;
      }
      // The presigned URL is valid for 15 min. Open in a new tab so the
      // browser handles the file natively (PDF preview, image view, etc.)
      window.open(resp.url, "_blank", "noopener,noreferrer");
    } catch (e) {
      setError((e && e.message) || "Download failed");
    }
  }

  async function handleDelete(doc) {
    if (!window.confirm(`Delete ${doc.original_filename}? This is permanent and audit-logged.`)) return;
    setError(null);
    try {
      const resp = await window.zignagenixAPI.invoke("db:lab-documents:delete", doc.id);
      if (resp && resp.error) {
        setError(resp.message || "Delete failed");
      } else {
        setSuccess(`Deleted ${doc.original_filename}`);
        await load();
      }
    } catch (e) {
      setError((e && e.message) || "Delete failed");
    }
  }

  return (
    <div className="lab-doc-attachments" style={{ padding: "12px 16px", background: "#f9fafc", borderTop: "1px solid #e1e6ed" }}>
      <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.4, color: "#5a6478", marginBottom: 8, display: "flex", alignItems: "center", gap: 6 }}>
        <FileText size={12} /> Attached documents ({docs.length})
      </div>

      {error && (
        <div style={alertStyle("error")}><AlertCircle size={14} /> {error}</div>
      )}
      {success && (
        <div style={alertStyle("ok")}><CheckCircle2 size={14} /> {success}</div>
      )}

      {/* Drop zone */}
      {canUpload && (
        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          onClick={() => !uploading && fileInputRef.current && fileInputRef.current.click()}
          style={{
            border: `2px dashed ${dragOver ? "#1f6feb" : "#c9d6e8"}`,
            background: dragOver ? "#e8f0fb" : "#fff",
            padding: 14, borderRadius: 4, textAlign: "center",
            cursor: uploading ? "default" : "pointer",
            color: "#5a6478", fontSize: 13,
            marginBottom: 10,
            display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
          }}
        >
          {uploading ? (
            <>
              <Loader2 size={16} className="spin" />
              <span>{progress === "reading" ? "Reading file…" : "Uploading…"}</span>
            </>
          ) : (
            <>
              <Upload size={16} />
              <span>Drop a file or click to upload — {ALLOWED_EXT_LABEL} up to 25 MB</span>
            </>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.jpg,.jpeg,.png,.tif,.tiff,.txt,.csv"
            onChange={handleFileInput}
            style={{ display: "none" }}
            disabled={uploading}
          />
        </div>
      )}

      {/* Existing attachments */}
      {loading ? (
        <div style={{ color: "#9aa4b2", fontSize: 12 }}>Loading…</div>
      ) : docs.length === 0 ? (
        <div style={{ color: "#9aa4b2", fontSize: 12, fontStyle: "italic" }}>No documents attached yet.</div>
      ) : (
        <table style={{ width: "100%", fontSize: 12, borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ color: "#5a6478", textAlign: "left" }}>
              <th style={th}>Filename</th>
              <th style={th}>Type</th>
              <th style={th}>Size</th>
              <th style={th}>Uploaded by</th>
              <th style={th}>When</th>
              <th style={{ ...th, textAlign: "right" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {docs.map((d) => (
              <tr key={d.id} style={{ borderTop: "1px solid #f0f3f7" }}>
                <td style={td}>
                  <FileText size={12} style={{ verticalAlign: "middle", marginRight: 4, color: "#5a6478" }} />
                  {d.original_filename}
                </td>
                <td style={{ ...td, color: "#5a6478" }}>{d.content_type}</td>
                <td style={{ ...td, color: "#5a6478" }}>{humanSize(d.size_bytes)}</td>
                <td style={{ ...td, color: "#5a6478" }}>{d.uploaded_by_name || d.uploaded_by_user_id}</td>
                <td style={{ ...td, color: "#5a6478" }} title={d.created_at}>
                  {new Date(d.created_at).toLocaleString()}
                </td>
                <td style={{ ...td, textAlign: "right" }}>
                  <button onClick={() => handleDownload(d.id, d.original_filename)} style={btnIcon} title="Download (presigned, 15-min expiry)">
                    <Download size={14} />
                  </button>
                  {canDelete && (
                    <button onClick={() => handleDelete(d)} style={{ ...btnIcon, color: "#b00020" }} title="Delete (soft delete + S3 object removed)">
                      <Trash2 size={14} />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <div style={{ marginTop: 8, fontSize: 10, color: "#9aa4b2" }}>
        Files encrypted at rest in zignagenix-phi S3 (SSE-AES256). Downloads use 15-minute presigned URLs.
        Every upload, download, and deletion writes to phi_access_log + audit_log (HIPAA §164.312(b)).
      </div>
    </div>
  );
}

const th = { padding: "4px 6px", fontWeight: 600, fontSize: 11 };
const td = { padding: "6px", verticalAlign: "middle" };
const btnIcon = {
  background: "transparent", border: "none", cursor: "pointer",
  color: "#5a6478", padding: "4px 6px",
};
function alertStyle(kind) {
  return {
    padding: "6px 10px", borderRadius: 3, fontSize: 12, marginBottom: 8,
    display: "flex", alignItems: "center", gap: 6,
    background: kind === "ok" ? "#e9f5e9" : "#fde8e8",
    border: kind === "ok" ? "1px solid #a5d5a5" : "1px solid #f5a5a5",
    color: kind === "ok" ? "#2d6b2d" : "#8b0000",
  };
}
